/* eslint-disable camelcase */
import get from 'lodash.get';
import stringSimilarity from 'string-similarity';
import uniq from 'lodash.uniq';
import isEmpty from 'lodash.isempty';
import moment from 'moment';
import {
    DUE_DATE_DATE_TYPE, DUE_DATE_DAYS_TYPE, MAX_DAYS, PACKAGE_EXPIRY_DAYS, SIGNATURE_V2_FEATURE, TAMPER_SEAL, TRANSACTION_PATH,
    VS_FEATURES
} from '../../constants';

export function textToNumber(e) {
    const {value} = e.target;
    const regExpr = /^[0-9]*$/;
    if (regExpr.test(value)) {
        return e;
    }
}

export function configurePackageDueDate({selectedTemplate, userAttributes}) {
    const expiryDays = Number(get(userAttributes, 'auto_expire_days', PACKAGE_EXPIRY_DAYS));
    const isAutoExpireEnabled = Boolean(get(userAttributes, 'enable_auto_expire'));
    if (!isEmpty(selectedTemplate)) {
        const templateExpiryDays = Number(get(selectedTemplate, 'days_to_expire')) || 0;
        if (templateExpiryDays > 0) {
            return moment().add(templateExpiryDays, 'days').toISOString();
        }
    }
    if (isEmpty(selectedTemplate) && isAutoExpireEnabled && expiryDays > 0) {
        return moment().add(expiryDays, 'days').toISOString();
    }
    return null;
}

export function checkIsWrongExpirationDate(selectedTemplate) {
    const templateExpiryDays = Number(get(selectedTemplate, 'days_to_expire')) || 0;
    const expiresAt = selectedTemplate?.expires_at;
    const templateHasWrongExpirationDate = templateExpiryDays <= 0
        && (moment(expiresAt).isBefore(moment(), 'day') || moment().isSame(expiresAt, 'day'));
    return !isEmpty(selectedTemplate) && templateHasWrongExpirationDate;
}

export function getMomentDateAtStartOfDay(date) {
    if (!date) return null;

    return moment(date).startOf('day');
}

export function getMomentDateAtEndOfDay(date) {
    if (!date) return null;

    return moment(date).endOf('day');
}

export function getISODateAtStartOfDay(date) {
    if (!date) return null;

    const calendarDate = moment(date).format('YYYY-MM-DD');
    return moment.utc(calendarDate).startOf('day').toISOString();
}

export function getISODateAtEndOfDay(date) {
    if (!date) return null;

    const calendarDate = moment(date).format('YYYY-MM-DD');
    return moment.utc(calendarDate).endOf('day').toISOString();
}

export function getMomentDateAtStartOfDayFromISOString(iso) {
    if (!iso) return null;

    const calendarDate = moment.utc(iso).format('YYYY-MM-DD');
    return moment(calendarDate).startOf('day');
}

export function getMomentDateAtEndOfDayFromISOString(iso) {
    if (!iso) return null;

    const calendarDate = moment.utc(iso).format('YYYY-MM-DD');
    return moment(calendarDate).endOf('day');
}

export function getSearchResults({options, search = '', optionKeys = []}) {
    const optionsResults = !isEmpty(options) ? options.filter((option) => {
        const optionKeysResults = optionKeys.map((key) => stringSimilarity
            .compareTwoStrings(option[key]?.toLowerCase() || '', search.toLowerCase()));
        const isSomeParamMatched = optionKeysResults.some((param) => param >= 0.3);
        return search === '' || search.length < 3 || isSomeParamMatched;
    }) : [];

    return optionsResults;
}

export function getVisibleApprovals(approvals = []) {
    return approvals.filter((approval) => !approval.id.includes(TAMPER_SEAL));
}

export function getRecipientsEmailsFromPackage(transactions = []) {
    if (!isEmpty(transactions)) {
        let emails = [];

        transactions.forEach((transaction) => {
            const recipients = get(transaction, 'recipients', []);
            emails = [...emails, ...recipients.map((r) => r.email)];
        });
        return uniq(emails.filter((email) => !!email));
    }
    return [];
}

export function getSendersEmailsFromPackages(transactions = []) {
    if (!isEmpty(transactions)) {
        let emails = [];

        transactions.forEach((transaction) => {
            const email = transaction?.sender?.email;
            emails = [...emails, email];
        });

        return uniq(emails.filter((email) => !!email));
    }
    return [];
}

export function getAvatarByEmail(avatars, email) {
    if (!isEmpty(avatars) && email) {
        const userAvatar = avatars.find((avatar) => avatar.email === email);

        if (userAvatar && userAvatar.avatarSrc) {
            return userAvatar.avatarSrc;
        }
    }
    return null;
}

export function getAvatarSrc(avatar) {
    if (!avatar) {
        return '';
    }
    if (typeof avatar === 'string') {
        return avatar;
    }
    if (avatar?.url) {
        return avatar.url;
    }
    if (avatar?.content) {
        if (avatar.content.startsWith('data:')) {
            return avatar.content;
        }
        return `data:image/jpeg;base64,${avatar.content}`;
    }
    return '';
}

/**
 * Converts a raw avatar binary (fetched from the user-service avatar endpoint)
 * into a data URL usable as an <img> source. Works in both browser and Node (SSR).
 * @param {ArrayBuffer|Buffer} buffer - Raw avatar bytes
 * @param {string} [contentType] - Response content type, used as the data URL mime
 * @returns {string} Data URL, or empty string when there is no avatar
 */
export function getAvatarDataUrl(buffer, contentType) {
    if (!buffer || buffer.byteLength === 0) {
        return '';
    }
    const mimeType = /^image\//.test(contentType) ? contentType : 'image/jpeg';
    let base64 = '';
    if (typeof Buffer !== 'undefined') {
        base64 = Buffer.from(buffer).toString('base64');
    } else {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) {
            binary += String.fromCharCode(bytes[i]);
        }
        base64 = window.btoa(binary);
    }
    if (!base64) {
        return '';
    }
    return `data:${mimeType};base64,${base64}`;
}

export function getOwnerAvatarSrc({ownerId, user}) {
    if (!ownerId) return '';
    if (user?.id === ownerId) return getAvatarSrc(user?.data?.avatar);
    return '';
}

export function getTransactionIdFromSignPath(str = '') {
    const result = str.match(new RegExp(`${TRANSACTION_PATH}/([^/?]+)`));
    return result ? result[1] : '';
}

export const redirectTo = (url) => {
    if (window?.__CEREMONY__?.parentRedirection) {
        window.parent.location.assign(url);
    } else {
        window.location.assign(url);
    }
};

export const hardRedirectTo = (url) => {
    if (window?.__CEREMONY__?.parentRedirection) {
        window.parent.location.replace(url);
    } else {
        window.location.replace(url);
    }
};
export const getVerificationToken = () => window?.__CEREMONY__?.verificationResponseToken;
export const isAdaEnabled = (transaction) => transaction.isAda;

export function isEmail(emailAdress) {
    // eslint-disable-next-line no-useless-escape
    const regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    if (emailAdress.match(regex)) return true;

    return false;
}

function hasEnvFeature({featureKey, env}) {
    if (featureKey === SIGNATURE_V2_FEATURE) {
        return true;
    }
    return false;
}

function hasVsFeature({id: featureKey, vsFeatures = []}) {
    return !!vsFeatures.find(({key}) => key === featureKey);
}

export function isVsFeatureEnabled({feature = {}, vsFeatures = [], env}) {
    const {
        id = '',
        isEnabledByDefault = false
    } = feature;
    const hasFeature = hasVsFeature({id, vsFeatures});

    if (env) {
        return hasEnvFeature({featureKey: id, env});
    }

    if (hasFeature) {
        return vsFeatures.some(({key, activated}) => key === id && activated);
    }

    return isEnabledByDefault;
}

export function getExpirationTimeType(packageData) {
    const remainingDays = get(packageData, 'settings.ceremony.remainingDays', 0);
    return remainingDays > 0 ? DUE_DATE_DAYS_TYPE : DUE_DATE_DATE_TYPE;
}

/**
 * Expiry mode derived from the persisted `days_to_expire` (the source of truth): a positive
 * value means the relative "days" mode, anything else means the fixed-date mode. Used to
 * initialize the settings-panel mode on load.
 */
export function getExpiryModeFromDaysToExpire(packageData) {
    return Number(get(packageData, 'days_to_expire')) > 0 ? DUE_DATE_DAYS_TYPE : DUE_DATE_DATE_TYPE;
}

/**
 * Whole-day count from today until a fixed expiry date, compared at calendar-day granularity.
 * `moment.diff(..., 'days')` truncates toward zero, so diffing raw timestamps drops the
 * partial day elapsed since the date was set and yields N-1. Anchoring both sides to
 * `startOf('day')` returns the calendar-day difference the user expects when a fixed
 * `expires_at` is converted back into a relative "expire in N days" count.
 */
export function getDaysUntilExpiry(expiresAt) {
    return moment(expiresAt).startOf('day').diff(moment().startOf('day'), 'days');
}

export function getExpirationErrors({
    packageData = {},
    expirationTimeType = getExpirationTimeType(packageData),
    getLongDate = () => {},
    getFormatErrorMessage = () => {},
    SHORT_DATE_FORMAT,
    date = packageData.expires_at

}) {
    const isWrongExpirationDate = checkIsWrongExpirationDate(packageData);
    const maxDate = moment().add(MAX_DAYS, 'days');
    const maxDateLabel = getLongDate(maxDate);
    const maxDateError = expirationTimeType === DUE_DATE_DATE_TYPE && moment(date).isAfter(maxDate);
    const minDateError = expirationTimeType === DUE_DATE_DATE_TYPE && isWrongExpirationDate;
    const isValid = !date || moment(moment(date), SHORT_DATE_FORMAT).isValid();
    return (minDateError || maxDateError || !isValid || isWrongExpirationDate) ? {
        minDateError,
        maxDateError,
        formatErrorLabel: isValid || !date ? '' : getFormatErrorMessage(),
        maxDateLabel,
        isWrongExpirationDate
    } : {};
}

export function getExpirationErrorMessages({
    minDateError, maxDateError, maxDateLabel, formatErrorLabel, intl
}) {
    if (formatErrorLabel) {
        return formatErrorLabel;
    } if (maxDateError) {
        return intl.formatMessage(
            {id: 'esl.error.validation.packageSettings.maximum_expiry_date_exceeded', defaultMessage: ''},
            {maxDate: maxDateLabel}
        );
    } if (minDateError) {
        return intl.formatMessage(
            {id: 'esl.error.validation.settingDueDate.pastDueDate', defaultMessage: ''}
        );
    }
    return null;
}

export function capitalizeFirstLetter(string) {
    return string[0].toUpperCase() + string.slice(1).toLowerCase();
}

export function sequentialAsync(values, callback) {
    return values.reduce(async (acc, value) => {
        const resultsObject = await acc;
        try {
            const result = await callback(value);
            return {
                ...resultsObject,
                results: [...resultsObject.results, result]
            };
        } catch (error) {
            return {
                ...resultsObject,
                errors: [...resultsObject.errors, error]
            };
        }
    }, Promise.resolve({
        results: [],
        errors: []
    }));
}
